import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { ListContainer, ListContext, Page, PageRoute, SearchBar, buildTemplatePath, pn } from "@compass/core";
import { ListFooter } from "components";
import { StaffPositionFilter, Permission, StaffPositionListDto, StaffPositionListResource } from "generated/openApi";
import { useInitListFilter, useList } from "hooks";
import ApiService from "services/apiService";
import { StaffPositionListPageHeader } from "./StaffPositionListPageHeader";
import { StaffPositionTable } from "./StaffPositionTable";

const routeName = "staffPositions";

export const staffPositionListPageRoute: PageRoute = {
    handle: "Staff Positions",
    templatePath: buildTemplatePath(routeName),
    getPath: () => buildTemplatePath(routeName),
};

export const StaffPositionListPage = () => {
    const ROOT_PERMISSION = Permission.SystemUtility;

    const [listItems, setListItems] = useState<StaffPositionListDto[]>([]);
    const { listFilter, setListFilter } = useInitListFilter<StaffPositionFilter>({
        sortObjects: [{ property: pn<StaffPositionListDto>("name"), isDesc: false }],
    });

    const [total, setTotal] = useState<number>(0);
    const [resource, setResource] = useState<StaffPositionListResource | null>(null);
    const [searchFocused, setSearchFocused] = useState<boolean>(false);

    const { tableContainerRef, applyPage, applyPageSize, loadList, applySort, applySearch, applySortMethod } = useList({
        listFilter,
        setListFilter,
        fetchList: async (filter) => {
            const data = await ApiService.staffPositions.getList(filter);
            setListItems(data || []);
            return data;
        },
        fetchTotal: async (filter) => {
            const total = await ApiService.staffPositions.getListTotal(filter);
            setTotal(total);
            return total;
        },
    });

    useEffect(() => {
        loadList(listFilter);
        ApiService.staffPositions.getListResource().then(setResource);
    }, []);

    return (
        <Page
            header={<StaffPositionListPageHeader rootPermission={ROOT_PERMISSION} handle={staffPositionListPageRoute.handle} />}
        >
            <ListContainer
                fullHeight
                header={<SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />}
                tableContainer={
                    resource && (
                        <StaffPositionTable
                            rootPermission={ROOT_PERMISSION}
                            tableContainerRef={tableContainerRef}
                            staffPositions={listItems}
                            resource={resource}
                            searchFocused={searchFocused}
                            sortObjects={listFilter.sortObjects}
                            sort={applySort}
                        />
                    )
                }
                footer={
                    <ListFooter
                        total={total}
                        size={listFilter.pageSize}
                        page={listFilter.page}
                        onPageChange={applyPage}
                        onSizeChange={applyPageSize}
                        onSortMethodChange={applySortMethod}
                    />
                }
            />
            <ListContext.Provider
                value={{
                    loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }),
                }}
            >
                <Outlet />
            </ListContext.Provider>
        </Page>
    );
};
